// Area facts per ZCTA from the American Community Survey — the data that
// makes a neighborhood page's content genuinely unique (the anti-doorway
// requirement). Committed under src/data/seo-geo/facts/ by the
// seo-geo:facts script; a SEPARATE freshness regime from snapshots: ACS
// 5-year estimates release annually (December) and must not entangle the
// daily price gate.
//
// Everything here is fail-closed: a regenerated facts file with impossible
// counts (a share numerator above its denominator, a negative estimate)
// breaks the build instead of rendering a wrong claim. ACS values are
// survey ESTIMATES with margins of error — display formatters phrase them
// as approximations, and pages must never make close-call comparisons
// between areas (the margins can swallow small differences).

import { formatInteger } from './format';

/** Raw ACS counts for one ZCTA. Counts, not percentages — shares are
 * derived with explicit denominators (same discipline as the FCC facts:
 * a share is meaningless without knowing its base). Field docs name the
 * source table cells so a refresh is auditable. */
export interface ZctaAcsCounts {
  /** B01003_001: total population. */
  population: number;
  /** B11001_001: total households. */
  households: number;
  /** B25003_001: occupied housing units (tenure universe). */
  occupiedUnits: number;
  /** B25003_003: renter-occupied units. */
  renterOccupied: number;
  /** B25024_001: all housing units (units-in-structure universe). */
  housingUnits: number;
  /** B25024_003: 1-unit attached — Philadelphia's rowhome stock. */
  units1Attached: number;
  /** B25024_006..009 summed: units in 5+ unit buildings. */
  units5Plus: number;
  /** B28002_001: households (internet-subscription universe). */
  internetUniverse: number;
  /** B28002_004: households with a broadband subscription of any type,
   * ANY provider — this is the honest "households served" figure; it is
   * never provider-specific and never derived from FCC coverage math. */
  broadbandHouseholds: number;
  /** B28002_013: households with no internet access. */
  noInternetHouseholds: number;
  /** B08301_001: workers 16 and over (commuting universe). */
  workers: number;
  /** B08301_021: worked from home. */
  workedFromHome: number;
}

export interface CityAcsFacts {
  schemaVersion: 1;
  /** Matches the GeoPageSnapshot slug this facts file serves. */
  slug: string;
  source: {
    name: string;
    /** e.g. "ACS 2024 5-year (2020-2024)". */
    release: string;
    /** When we pulled it, ISO date. */
    retrievedAt: string;
    /** Retrieval channel; production refreshes should hit api.census.gov
     * directly (with a key) and add margins of error. */
    via: string;
  };
  maintenance: string;
  zctas: Record<string, ZctaAcsCounts>;
}

/**
 * Housing-stock archetype — the branch point for genuinely different page
 * copy (a high-rise area and a rowhome area get different guidance, not the
 * same sentence with numbers swapped). Derived from structure shares of the
 * SAME universe (housingUnits), so the two can only both clear 0.5 when
 * they sum to exactly 1.0 — high-rise wins that tie by declaration order.
 */
export type HousingArchetype = 'high-rise' | 'rowhome' | 'mixed';

export interface AreaProfile {
  population: number;
  households: number;
  /** All shares are 0..1 fractions of their documented universes. */
  renterShare: number;
  apartmentShare: number;
  rowhomeShare: number;
  broadbandShare: number;
  noInternetShare: number;
  workFromHomeShare: number;
  archetype: HousingArchetype;
}

const HIGH_RISE_THRESHOLD = 0.5;
const ROWHOME_THRESHOLD = 0.5;

function requireCount(value: number, name: string, zcta: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(
      `areaFacts: ${zcta} ${name} is not a non-negative count: ${value}`
    );
  }
  return value;
}

function share(
  numerator: number,
  denominator: number,
  name: string,
  zcta: string
): number {
  if (denominator <= 0) {
    throw new Error(
      `areaFacts: ${zcta} ${name} has an empty universe (${denominator})`
    );
  }
  if (numerator > denominator) {
    throw new Error(
      `areaFacts: ${zcta} ${name} numerator ${numerator} exceeds its universe ${denominator}`
    );
  }
  return numerator / denominator;
}

export function deriveAreaProfile(
  counts: ZctaAcsCounts,
  zcta: string
): AreaProfile {
  const keys: Array<keyof ZctaAcsCounts> = [
    'population',
    'households',
    'occupiedUnits',
    'renterOccupied',
    'housingUnits',
    'units1Attached',
    'units5Plus',
    'internetUniverse',
    'broadbandHouseholds',
    'noInternetHouseholds',
    'workers',
    'workedFromHome',
  ];
  for (const key of keys) {
    requireCount(counts[key], key, zcta);
  }

  const apartmentShare = share(
    counts.units5Plus,
    counts.housingUnits,
    'apartmentShare',
    zcta
  );
  const rowhomeShare = share(
    counts.units1Attached,
    counts.housingUnits,
    'rowhomeShare',
    zcta
  );

  let archetype: HousingArchetype = 'mixed';
  if (apartmentShare >= HIGH_RISE_THRESHOLD) {
    archetype = 'high-rise';
  } else if (rowhomeShare >= ROWHOME_THRESHOLD) {
    archetype = 'rowhome';
  }

  return {
    population: counts.population,
    households: counts.households,
    renterShare: share(
      counts.renterOccupied,
      counts.occupiedUnits,
      'renterShare',
      zcta
    ),
    apartmentShare,
    rowhomeShare,
    broadbandShare: share(
      counts.broadbandHouseholds,
      counts.internetUniverse,
      'broadbandShare',
      zcta
    ),
    noInternetShare: share(
      counts.noInternetHouseholds,
      counts.internetUniverse,
      'noInternetShare',
      zcta
    ),
    workFromHomeShare: share(
      counts.workedFromHome,
      counts.workers,
      'workFromHomeShare',
      zcta
    ),
    archetype,
  };
}

/**
 * ACS estimates carry margins of error — population renders as an
 * approximation ("about 25,900"), never an exact count.
 */
export function formatApproxCount(value: number): string {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`formatApproxCount: not a non-negative count: ${value}`);
  }
  return `about ${formatInteger(Math.round(value / 100) * 100)}`;
}

/** 0..1 fraction → whole-percent string ("84%").
 *
 * Copy-wiring note: archetype classification uses the UNROUNDED share.
 * A page must classify and display from the same value — rendering a
 * rounded "50%" beside a "mixed" label (actual 0.495) reads as a
 * contradiction. When wiring neighborhood copy, show the share the
 * archetype was derived from. */
export function formatSharePct(fraction: number): string {
  if (!Number.isFinite(fraction) || fraction < 0 || fraction > 1) {
    throw new Error(`formatSharePct: not a 0..1 fraction: ${fraction}`);
  }
  return `${Math.round(fraction * 100)}%`;
}

/**
 * Housing-mix cell copy, honoring the copy-wiring note above: the label
 * shows exactly the share(s) the archetype was derived from. A mixed area
 * shows both structure shares (larger first) so the absence of a majority
 * is visible rather than asserted.
 */
export function housingMixLabel(profile: AreaProfile): string {
  if (profile.archetype === 'high-rise') {
    return `${formatSharePct(profile.apartmentShare)} apartment buildings`;
  }
  if (profile.archetype === 'rowhome') {
    return `${formatSharePct(profile.rowhomeShare)} rowhomes`;
  }
  const apartments = `${formatSharePct(profile.apartmentShare)} apartments`;
  const rowhomes = `${formatSharePct(profile.rowhomeShare)} rowhomes`;
  return profile.apartmentShare >= profile.rowhomeShare
    ? `mixed: ${apartments}, ${rowhomes}`
    : `mixed: ${rowhomes}, ${apartments}`;
}

/**
 * Pool raw counts across a target's ZCTAs into one set of counts, so a
 * multi-ZIP target gets a single profile derived by the same fail-closed
 * math as a single ZIP. Summing raw counts (never averaging shares) keeps
 * every universe honest.
 */
export function pooledZctaCounts(
  countsList: ZctaAcsCounts[],
  label: string
): ZctaAcsCounts {
  if (countsList.length === 0) {
    throw new Error(`areaFacts: ${label} has no ZCTA counts to pool`);
  }
  const keys: (keyof ZctaAcsCounts)[] = [
    'population',
    'households',
    'occupiedUnits',
    'renterOccupied',
    'housingUnits',
    'units1Attached',
    'units5Plus',
    'internetUniverse',
    'broadbandHouseholds',
    'noInternetHouseholds',
    'workers',
    'workedFromHome',
  ];
  const pooled = {} as ZctaAcsCounts;
  for (const key of keys) {
    pooled[key] = countsList.reduce(
      (sum, counts) => sum + requireCount(counts[key], key, label),
      0
    );
  }
  return pooled;
}

export interface AreaContentThresholds {
  /** Share delta (0..1 fraction) at or above which two areas differ. */
  sharePts: number;
  /** Population ratio (larger/smaller) at or above which two areas differ. */
  populationRatio: number;
}

/** ADR-021: set a priori at "material to a mover", not fitted to the
 * current roster. The canary is 19134 vs 19145 (both rowhome, shares
 * within 7 points) which passes on population ratio 1.23 alone. */
export const DEFAULT_AREA_CONTENT_THRESHOLDS: AreaContentThresholds = {
  sharePts: 0.1,
  populationRatio: 1.2,
};

/**
 * ADR-021 content dimension for the anti-doorway gate: at neighborhood
 * scale offers are uniform on purpose, so sibling pages earn separate
 * existence through area content instead. Two profiles differ when the
 * housing archetype flips, any share moves by sharePts, or population
 * differs by populationRatio.
 */
export function areaProfilesDiffer(
  a: AreaProfile,
  b: AreaProfile,
  thresholds: AreaContentThresholds = DEFAULT_AREA_CONTENT_THRESHOLDS
): boolean {
  if (a.archetype !== b.archetype) return true;
  const shares: (keyof Pick<
    AreaProfile,
    | 'apartmentShare'
    | 'rowhomeShare'
    | 'renterShare'
    | 'broadbandShare'
    | 'workFromHomeShare'
  >)[] = [
    'apartmentShare',
    'rowhomeShare',
    'renterShare',
    'broadbandShare',
    'workFromHomeShare',
  ];
  for (const key of shares) {
    if (Math.abs(a[key] - b[key]) >= thresholds.sharePts) return true;
  }
  const larger = Math.max(a.population, b.population);
  const smaller = Math.min(a.population, b.population);
  if (smaller <= 0) {
    // An unpopulated ZCTA cannot content-differentiate a sales page.
    return false;
  }
  return larger / smaller >= thresholds.populationRatio;
}
